import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ShoppingBag, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const { cartCount } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { key: 'nav.agency', href: '/#about' },
    { key: 'nav.portfolio', href: '/#portfolio' },
    { key: 'nav.baoBrunch', href: '/#services' },
    { key: 'nav.contact', href: '/#contact' },
    { key: 'nav.legal', href: '/legal' },
  ];

  const handleMenuClick = (href) => {
    setIsMenuOpen(false);
    if (href.startsWith('/#')) {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(href.replace('/', ''));
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      navigate(href);
    }
  };

  return (
    <>
      <motion.header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-sm py-3' 
            : 'bg-transparent py-5'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        data-testid="main-header"
      >
        <div className="container-custom flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className={`font-serif font-bold text-xl md:text-2xl tracking-tight transition-colors ${
              scrolled ? 'text-black' : 'text-white'
            }`}
            data-testid="logo-link"
          >
            BAO AGENCY
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {menuItems.slice(0, 4).map((item, index) => (
              <motion.button
                key={item.key}
                onClick={() => handleMenuClick(item.href)}
                className={`relative font-sans text-sm font-medium transition-colors ${
                  scrolled ? 'text-neutral-600 hover:text-black' : 'text-white/80 hover:text-white'
                }`}
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                data-testid={`nav-${item.key.split('.')[1]}`}
              >
                {t(item.key)}
                <motion.span 
                  className={`absolute -bottom-1 left-0 h-0.5 ${scrolled ? 'bg-black' : 'bg-white'}`}
                  initial={{ width: 0 }}
                  whileHover={{ width: '100%' }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                scrolled ? 'text-neutral-600 hover:text-black' : 'text-white/80 hover:text-white'
              }`}
              data-testid="language-switcher"
            >
              <Globe size={18} />
              <span className="hidden sm:inline">{language.toUpperCase()}</span>
            </button>

            {/* Cart */}
            <Link 
              to="/cart" 
              className={`relative p-2 rounded-full transition-colors ${
                scrolled ? 'hover:bg-neutral-100' : 'hover:bg-white/10'
              }`}
              data-testid="cart-link"
            >
              <ShoppingBag size={20} className={scrolled ? 'text-black' : 'text-white'} />
              {cartCount > 0 && (
                <span 
                  className="absolute -top-1 -right-1 bg-black text-white text-xs w-5 h-5 rounded-full flex items-center justify-center"
                  data-testid="cart-count"
                >
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(true)}
              className={`lg:hidden p-2 rounded-full transition-colors ${
                scrolled ? 'hover:bg-neutral-100' : 'hover:bg-white/10'
              }`}
              data-testid="mobile-menu-button"
            >
              <Menu size={24} className={scrolled ? 'text-black' : 'text-white'} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center"
            data-testid="mobile-menu-overlay"
          >
            <button
              onClick={() => setIsMenuOpen(false)}
              className="absolute top-6 right-6 p-2 hover:bg-neutral-100 rounded-full"
              data-testid="close-menu-button"
            >
              <X size={28} />
            </button>

            <nav className="flex flex-col items-center gap-8">
              {menuItems.map((item, index) => (
                <motion.button
                  key={item.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => handleMenuClick(item.href)}
                  className="font-serif text-3xl md:text-4xl font-semibold hover:text-neutral-500 transition-colors"
                  data-testid={`mobile-nav-${item.key.split('.')[1]}`}
                >
                  {t(item.key)}
                </motion.button>
              ))}
            </nav>

            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              onClick={() => {
                toggleLanguage();
                setIsMenuOpen(false);
              }}
              className="mt-12 flex items-center gap-2 text-lg font-medium text-neutral-500 hover:text-black transition-colors"
              data-testid="mobile-language-switcher"
            >
              <Globe size={20} />
              {language === 'fr' ? 'English' : 'Français'}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
